import { CapabilityAdministrationPanel } from '@/components/panels/CapabilityAdministrationPanel'
import type React from 'react'
import { useCallback } from 'react'
import {useDashboardNavigation} from '@/hooks/useDashboardNavigation'
import { Sparkles, MessageSquare, Plus, PanelLeftOpen } from 'lucide-react'
import { useAppStore } from '@/store'
import { getToken } from '@/lib/auth'
import { DashboardView } from '@/components/dashboard/DashboardView'
import { CommandCentrePanel } from '@/components/panels/CommandCentrePanel'
import { GovernancePanel } from '@/components/panels/GovernancePanel'
import { SecurityPanel } from '@/components/panels/SecurityPanel'
import { WorkersPanel } from '@/components/panels/WorkersPanel'
import { TenantsPanel } from '@/components/panels/TenantsPanel'
import { MemoryPanel } from '@/components/panels/MemoryPanel'
import { MCPPanel } from '@/components/panels/MCPPanel'
import { AuditPanel } from '@/components/panels/AuditPanel'
import { KnowledgePanel } from '@/components/panels/KnowledgePanel'
import { DriftPanel } from '@/components/panels/DriftPanel'
import { PlaybooksPanel } from '@/components/panels/PlaybooksPanel'
import { ToolsRegistryPanel } from '@/components/panels/ToolsRegistryPanel'
import { OpsBridgeFeaturesPanel } from '@/components/panels/OpsBridgeFeaturesPanel'
import { SkillsRegistryPanel } from '@/components/panels/SkillsRegistryPanel'
import { SessionHistoryPanel } from '@/components/panels/SessionHistoryPanel'
import { ImagesPanel } from '@/components/panels/ImagesPanel'
import { LibraryPanel } from '@/components/panels/LibraryPanel'
import { WordPressPanel } from '@/components/panels/WordPressPanel'
import { DownloadCenterPanel } from '@/components/panels/DownloadCenterPanel'
import { ExtensionActivitiesPanel } from '@/components/panels/ExtensionActivitiesPanel'
import { LibrarianPanel } from '@/components/panels/LibrarianPanel'
import { ActionLogPanel } from '@/components/panels/ActionLogPanel'
import { EngineeringActivityPanel } from '@/components/panels/EngineeringActivityPanel'
import { TaskListPanel } from '@/components/panels/TaskListPanel'
import { SelfAuditPanel } from '@/components/panels/SelfAuditPanel'
import { RoutesPanel } from '@/components/panels/RoutesPanel'
import { LicensingPanel } from '@/components/panels/LicensingPanel'
import { StaffPanel } from '@/components/panels/StaffPanel'
import { DeploymentApprovalsPanel } from '@/components/panels/DeploymentApprovalsPanel'
import { BillingPanel } from '@/components/panels/BillingPanel'
import { GitHubIntegrationPanel } from '@/components/panels/GitHubIntegrationPanel'
import { CloudflareConnectionPanel } from '@/components/panels/CloudflareConnectionPanel'
import { BuildReviewPanel } from '@/components/panels/BuildReviewPanel'
import { PipelinePanel } from '@/components/panels/PipelinePanel'
import { PlatformModelPanel } from '@/components/panels/PlatformModelPanel'
import { BrowserLivePanel } from '@/components/panels/BrowserLivePanel'
import { ArchitecturePanel } from '@/components/panels/ArchitecturePanel'
import { SandboxPanel } from '@/components/panels/SandboxPanel'
import { AppearancePanel } from '@/components/panels/AppearancePanel'
import { MyProfilePanel } from '@/components/panels/MyProfilePanel'
import { StaffProfilesPanel } from '@/components/panels/StaffProfilesPanel'
import { KnowledgeAuditPanel } from '@/components/panels/KnowledgeAuditPanel'
import { AuditCentrePanel } from '@/components/panels/AuditCentrePanel'
import { ChatPanel } from '@/components/chat/ChatPanel'
import { ChatsPanel } from '@/components/chat/ChatsPanel'

function StubPanel({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-center h-64 text-text-muted">
      <div className="text-center">
        <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-30" />
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs mt-1 opacity-60">Coming in Phase 2</div>
      </div>
    </div>
  )
}

const PANEL_MAP: Record<string, React.ComponentType> = {
  commandcentre: CommandCentrePanel,
  dashboard: DashboardView,
  governance: GovernancePanel,
  security: SecurityPanel,
  workers: WorkersPanel,
  tenants: TenantsPanel,
  knowledge: KnowledgePanel,
  drift: DriftPanel,
  playbooks: PlaybooksPanel,
  platformfeatures: ToolsRegistryPanel,
  opsbridgefeatures: OpsBridgeFeaturesPanel,
  tier3override: GovernancePanel,
  toolsregistry: ToolsRegistryPanel,
  skillsregistry: SkillsRegistryPanel,
  memory: MemoryPanel,
  mcp: MCPPanel,
  audit: AuditPanel,
  sessions: SessionHistoryPanel,
  images: ImagesPanel,
  library: LibraryPanel,
  wordpress: WordPressPanel,
  downloads: DownloadCenterPanel,
  extensionactivities: ExtensionActivitiesPanel,
  librarian: LibrarianPanel,
  actionlog: ActionLogPanel,
  activity: EngineeringActivityPanel,
  tasks: TaskListPanel,
  selfaudit: SelfAuditPanel,
  routes: RoutesPanel,
  toolpermissions: GovernancePanel,
  tenantadmin: LicensingPanel,
  team: StaffPanel,
  deployapprovals: DeploymentApprovalsPanel,
  billing: BillingPanel,
  github: GitHubIntegrationPanel,
  cloudflare: CloudflareConnectionPanel,
  buildreviews: BuildReviewPanel,
  pipeline: PipelinePanel,
  platformmodel: PlatformModelPanel,
  browserlive: BrowserLivePanel,
  architecture: ArchitecturePanel,
  sandbox: SandboxPanel,
  appearance: AppearancePanel,
  myprofile: MyProfilePanel,
  staffprofiles: StaffProfilesPanel,
  capabilitycontrol: CapabilityAdministrationPanel,
  auditcentre: AuditCentrePanel,
  users: () => <StubPanel title="Users" />,
  subscriptions: () => <StubPanel title="Subscriptions" />,
  recommendations: KnowledgeAuditPanel,
  trends: () => <StubPanel title="Trends" />,
}

interface MainContentProps {
  isMobile?: boolean
  onOpenMobileSidebar?: () => void
}

export function MainContent({ isMobile, onOpenMobileSidebar }: MainContentProps) {
  const requestedSection = useAppStore((s) => s.activeSection)
  const navigation=useDashboardNavigation()
  const activeSection=navigation.canSee(requestedSection)?requestedSection:'chat'
  const activeSessionId = useAppStore((s) => s.activeSessionId)
  const chatPanelCollapsed = useAppStore((s) => s.chatPanelCollapsed)
  const toggleChatPanel = useAppStore((s) => s.toggleChatPanel)
  const setActiveSessionId = useAppStore((s) => s.setActiveSessionId)
  const setSelectedChatSessionId = useAppStore((s) => s.setSelectedChatSessionId)
  const setChatMessages = useAppStore((s) => s.setChatMessages)
  const bumpSessionList = useAppStore((s) => s.bumpSessionList)

  const INIT_MSG = { id: 'msg_init', role: 'system' as const, content: 'Connected to Ops-Bridge. Live endpoint mode.', timestamp: new Date() }

  const handleNewChat = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboard/sessions', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'New Chat' }),
      }).then(res => res.json())
      if (r.success && r.session) {
        setActiveSessionId(r.session.id)
        setChatMessages([INIT_MSG])
        setSelectedChatSessionId(r.session.id)
        bumpSessionList()
      }
    } catch { /* silent */ }
  }, [setActiveSessionId, setChatMessages, setSelectedChatSessionId, bumpSessionList])

  if(!navigation.ready)return <div className="p-6 text-sm text-text-muted">Workspace access is being checked. If this persists, reconnect to your session.</div>

  if (activeSection === 'chat') {
    const showPanel = !chatPanelCollapsed && (!isMobile || !activeSessionId)
    return (
      <div className="flex flex-1 h-full overflow-hidden relative">
        {showPanel && (
          isMobile
            ? <div className="fixed inset-0 z-40"><ChatsPanel isMobile onBackToSidebar={onOpenMobileSidebar} /></div>
            : <ChatsPanel />
        )}

        <div className="flex-1 min-w-0 flex flex-col h-full overflow-hidden">
          {activeSessionId ? (
            <div className="flex flex-1 h-full overflow-hidden">
              <div className="flex-1 min-w-0">
                <ChatPanel />
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-text-muted gap-3 relative">
              {/* Expand panel button — shown when panel is collapsed */}
              {chatPanelCollapsed && (
                <button
                  type="button"
                  onClick={toggleChatPanel}
                  className="absolute top-4 left-4 p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-elevated active:bg-surface-elevated transition-colors"
                  title="Show chat history"
                  aria-label="Expand chat panel"
                >
                  <PanelLeftOpen className="h-4 w-4" />
                </button>
              )}
              <MessageSquare className="h-10 w-10 opacity-20" />
              <p className="text-[18px] font-medium text-text-primary">Select a chat or start a new one</p>
              <p className="text-[16px] text-text-primary">Your conversations will appear on the left</p>
              {/* Floating New Chat button — bottom right */}
              <button
                type="button"
                onClick={handleNewChat}
                className="absolute bottom-6 right-6 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-accent text-white text-sm font-semibold shadow-lg hover:opacity-90 active:opacity-80 transition-opacity"
              >
                <Plus className="h-4 w-4" />
                New Chat
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  const FULLSCREEN_PANELS = new Set(['commandcentre'])
  const Panel = PANEL_MAP[activeSection] ?? CommandCentrePanel

  if (FULLSCREEN_PANELS.has(activeSection)) {
    return (
      <div className="flex flex-1 h-full overflow-hidden">
        <div className="h-full w-full">
          <Panel />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-1 h-full overflow-hidden">
      <div
        data-scroll-container
        className="h-full w-full overflow-y-auto overflow-x-hidden"
        style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', position: 'relative' }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '100%',
            minHeight: '100%',
            boxSizing: 'border-box',
            padding: ['wordpress', 'governance', 'tier3override', 'toolpermissions', 'team', 'staffprofiles', 'myprofile'].includes(activeSection)
              ? 0
              : 'var(--main-content-padding, 16px)',
            overflowX: 'hidden',
          }}
        >
          <Panel />
        </div>
      </div>
    </div>
  )
}
